import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useTenant } from '@/hooks/use-tenant';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Info,
  Star,
  Settings,
  Filter,
  Archive,
  Trash2,
  Send,
  CalendarClock,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  date: string;
  read: boolean;
  type: 'welcome' | 'profile' | 'tutorial' | 'renewal' | 'cost' | 'system' | 'license' | 'application';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  category?: string;
  actionUrl?: string;
}

interface RenewalReminder {
  licenseId: number;
  licenseName: string;
  applicationName: string;
  renewalDate: string;
  daysUntil: number;
  cost: number | null;
  priority: 'medium' | 'high' | 'urgent';
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>([]);
  const [notifiedLicenses, setNotifiedLicenses] = useState<Set<number>>(new Set());

  // Fetch renewal reminders (30-day window, for existing logic)
  const { data: renewalReminders = [] } = useQuery<any[]>({
    queryKey: ['/api/notifications/renewal-reminders'],
    enabled: !!user,
  });

  // Fetch upcoming renewals for the Renewals management tab (90-day window)
  const { data: upcomingRenewals = [], isLoading: renewalsLoading } = useQuery<RenewalReminder[]>({
    queryKey: ['/api/notifications/renewal-reminders', { days: 90 }],
    queryFn: async () => {
      const res = await fetch('/api/notifications/renewal-reminders?days=90');
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!user,
  });

  // Send manual renewal reminder to all team members
  const sendReminderMutation = useMutation({
    mutationFn: async (licenseId: number) => {
      return apiRequest('/api/notifications/send-renewal-reminder', {
        method: 'POST',
        body: { licenseId },
        headers: { 'Content-Type': 'application/json' },
      });
    },
    onSuccess: (data: any, licenseId) => {
      setNotifiedLicenses(prev => new Set([...prev, licenseId]));
      toast({
        title: 'Reminder sent',
        description: `${data.sent} team member${data.sent === 1 ? '' : 's'} notified about "${data.licenseName}".`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/user'] });
    },
    onError: () => {
      toast({
        title: 'Failed to send reminder',
        description: 'Could not notify team members. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Merge renewal reminders into notifications list
  useEffect(() => {
    if (!user) return;

    const storageKey = `notifications_tenant_${user.tenantId || user.id}`;
    let base: NotificationItem[] = [];

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        base = JSON.parse(saved).filter((n: NotificationItem) => n.type !== 'renewal');
      }
    } catch {
      // ignore
    }

    // Build renewal notifications from real backend data
    const renewalItems: NotificationItem[] = (renewalReminders as any[]).map((r, idx) => ({
      id: 90000 + idx,
      title: `License Renewal: ${r.applicationName}`,
      message: `"${r.licenseName}" renews in ${r.daysUntil === 0 ? 'today' : `${r.daysUntil} day${r.daysUntil === 1 ? '' : 's'}`}${r.cost ? ` — $${r.cost.toLocaleString()}` : ''}.`,
      date: r.daysUntil === 0 ? 'Today' : r.daysUntil <= 1 ? 'Tomorrow' : `In ${r.daysUntil} days`,
      read: false,
      type: 'renewal' as const,
      priority: r.priority,
      category: 'Renewals',
      actionUrl: '/licenses'
    }));

    // Keep static non-renewal notifications
    if (base.length === 0) {
      const tenantName = tenant?.name || "Your organization";
      base = [
        {
          id: 1,
          title: 'Welcome to SaaSGama',
          message: `${tenantName}: Get started by adding your first application and license`,
          date: 'Today',
          read: false,
          type: 'welcome',
          priority: 'high',
          category: 'Onboarding'
        },
        {
          id: 3,
          title: 'Cost Optimization',
          message: 'Review your license utilization to identify savings opportunities.',
          date: 'This week',
          read: true,
          type: 'cost',
          priority: 'medium',
          category: 'Cost Management',
          actionUrl: '/cost-management'
        }
      ];
    }

    const merged = [...renewalItems, ...base].sort((a, b) => {
      const p: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };
      return (p[b.priority ?? 'low'] ?? 1) - (p[a.priority ?? 'low'] ?? 1);
    });

    setNotifications(merged);
  }, [user, tenant, renewalReminders]);


  const markAsRead = (notificationId: number) => {
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      
      // Save to localStorage
      const storageKey = `notifications_tenant_${user?.tenantId || user?.id}`;
      localStorage.setItem(storageKey, JSON.stringify(updated));
      
      return updated;
    });
  };

  const markAllAsRead = () => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      
      // Save to localStorage
      const storageKey = `notifications_tenant_${user?.tenantId || user?.id}`;
      localStorage.setItem(storageKey, JSON.stringify(updated));
      
      return updated;
    });

    toast({
      description: "All notifications marked as read",
    });
  };

  const deleteNotification = (notificationId: number) => {
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      
      // Save to localStorage
      const storageKey = `notifications_tenant_${user?.tenantId || user?.id}`;
      localStorage.setItem(storageKey, JSON.stringify(updated));
      
      return updated;
    });

    toast({
      description: "Notification deleted",
    });
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    markAsRead(notification.id);
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const getNotificationIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'welcome':
      case 'tutorial':
        return <Star className="w-5 h-5 text-purple-600" />;
      case 'renewal':
        return <Clock className="w-5 h-5 text-orange-600" />;
      case 'cost':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'system':
        return <Info className="w-5 h-5 text-blue-600" />;
      case 'license':
        return <Settings className="w-5 h-5 text-green-600" />;
      case 'application':
        return <Star className="w-5 h-5 text-indigo-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'default';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    switch (activeTab) {
      case 'unread':
        return !notification.read;
      case 'read':
        return notification.read;
      case 'important':
        return notification.priority === 'urgent' || notification.priority === 'high';
      default:
        return true;
    }
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">
            Stay updated with important alerts and system messages
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {unreadCount > 0 && (
            <Button onClick={markAllAsRead} variant="outline" size="sm">
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all read
            </Button>
          )}
          <Badge variant="secondary">
            {unreadCount} unread
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="all">
            All ({notifications.length})
          </TabsTrigger>
          <TabsTrigger value="unread">
            Unread ({unreadCount})
          </TabsTrigger>
          <TabsTrigger value="important">
            Important
          </TabsTrigger>
          <TabsTrigger value="read">
            Read
          </TabsTrigger>
          <TabsTrigger value="renewals" className="flex items-center gap-1">
            <CalendarClock className="w-3.5 h-3.5" />
            Renewals
            {upcomingRenewals.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0 h-4">
                {upcomingRenewals.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Renewals management tab */}
        <TabsContent value="renewals" className="mt-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-orange-500" />
                Upcoming Renewals (next 90 days)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Click "Notify Team" to send an in-app reminder to all users in your organization for a specific license.
              </p>
            </CardHeader>
            <CardContent>
              {renewalsLoading ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Loading renewals…</p>
              ) : upcomingRenewals.length === 0 ? (
                <div className="py-10 text-center">
                  <CalendarClock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">No renewals in the next 90 days</p>
                  <p className="text-xs text-gray-500 mt-1">All your licenses are up to date.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {upcomingRenewals.map((renewal) => {
                    const alreadySent = notifiedLicenses.has(renewal.licenseId);
                    const urgencyColor =
                      renewal.priority === 'urgent' ? 'text-red-600 bg-red-50 border-red-200' :
                      renewal.priority === 'high' ? 'text-orange-600 bg-orange-50 border-orange-200' :
                      'text-blue-600 bg-blue-50 border-blue-200';
                    const daysLabel =
                      renewal.daysUntil === 0 ? 'Today' :
                      renewal.daysUntil === 1 ? 'Tomorrow' :
                      `${renewal.daysUntil} days`;

                    return (
                      <div key={renewal.licenseId} className="flex items-center justify-between py-3 gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`shrink-0 rounded-md px-2 py-1 text-xs font-semibold border ${urgencyColor}`}>
                            {daysLabel}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{renewal.applicationName}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {renewal.licenseName}
                              {renewal.cost ? ` — $${renewal.cost.toLocaleString()}` : ''}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={alreadySent ? 'outline' : 'default'}
                          disabled={alreadySent || sendReminderMutation.isPending}
                          onClick={() => sendReminderMutation.mutate(renewal.licenseId)}
                          className="shrink-0"
                        >
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                          {alreadySent ? 'Sent' : 'Notify Team'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {activeTab !== 'renewals' && <TabsContent value={activeTab} className="mt-6">
          <div className="space-y-4">
            {filteredNotifications.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BellOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No notifications
                  </h3>
                  <p className="text-gray-600">
                    {activeTab === 'unread' 
                      ? "You're all caught up! No unread notifications."
                      : `No ${activeTab} notifications found.`
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredNotifications.map((notification) => (
                <Card 
                  key={notification.id}
                  className={`transition-all hover:shadow-md cursor-pointer ${
                    !notification.read 
                      ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className={`text-sm font-medium ${
                              !notification.read ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {notification.message}
                            </p>
                            <div className="flex items-center space-x-3 mt-3">
                              <span className="text-xs text-gray-500">
                                {notification.date}
                              </span>
                              {notification.category && (
                                <Badge variant="outline" className="text-xs">
                                  {notification.category}
                                </Badge>
                              )}
                              {notification.priority && (
                                <Badge 
                                  variant={getPriorityColor(notification.priority)}
                                  className="text-xs"
                                >
                                  {notification.priority}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-2 ml-4">
                            {!notification.read && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>}
      </Tabs>
    </div>
  );
}